import { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ledgerService } from '../../services/ledger/ledgerService';
import type { 
  ReverseJournalResponse 
} from '../../services/ledger/ledgerService';

export interface JournalFilters {
  periodName?: string;
  status?: 'DRAFT' | 'POSTED' | 'REVERSED';
  externalRef?: string;
}

interface Journal {
  id: string;
  externalRef: string;
  description: string;
  journalDate: string;
  status: 'DRAFT' | 'POSTED' | 'REVERSED';
  totalAmount: string;
  postedAt?: string | null;
  reversedAt?: string | null;
  reversalRef?: string | null;
}

export interface UseLedgerJournalsReturn {
  journals: Journal[];
  loading: boolean;
  error: string | null;
  filters: JournalFilters;
  setFilters: (filters: JournalFilters) => void;
  cursor: string | null;
  nextCursor: string | null;
  hasNextPage: boolean;
  total: number;
  
  // Actions
  loadMore: () => void;
  refreshJournals: () => Promise<void>;
  reverseJournal: (journalId: string, reason: string) => Promise<ReverseJournalResponse>;
  
  // Optimistic updates
  updateJournalOptimistically: () => void;
}

export function useLedgerJournals(): UseLedgerJournalsReturn {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<JournalFilters>({});
  const [cursor, setCursor] = useState<string | null>(null);

  // Build query parameters
  const queryParams: JournalFilters = {
    ...filters,
  };

  // Fetch journals query
  const {
    data: currentPageData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['ledger', 'journals', filters.periodName, filters.status, filters.externalRef],
    queryFn: () => ledgerService.getJournals(queryParams),
    staleTime: 30000, // 30 seconds
    refetchInterval: 30000, // Poll every 30 seconds (only for journals list)
    enabled: true, // Ensure query is enabled
  });

  // Use data directly from React Query instead of maintaining separate state
  const journals = currentPageData?.journals || [];
  const total = currentPageData?.pagination?.total || 0;

  // Reset cursor when filters change
  useEffect(() => {
    setCursor(null);
  }, [filters]);

  // Reverse journal mutation
  const reverseJournalMutation = useMutation({
    mutationFn: ({ journalId, reason }: { journalId: string; reason: string }) =>
      ledgerService.reverseJournal(journalId, reason),
    onSuccess: () => {
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['ledger', 'journals'] });
      queryClient.invalidateQueries({ queryKey: ['ledger', 'reports'] });
      
      // Optimistically update by invalidating queries
      updateJournalOptimistically();
    },
  });

  // Actions
  const loadMore = useCallback(() => {
    if (currentPageData?.pagination?.nextCursor) {
      setCursor(currentPageData.pagination.nextCursor);
    }
  }, [currentPageData?.pagination?.nextCursor]);

  const refreshJournals = useCallback(async () => {
    setCursor(null);
    await refetch();
  }, [refetch]);

  const reverseJournal = useCallback(async (journalId: string, reason: string) => {
    return reverseJournalMutation.mutateAsync({ journalId, reason });
  }, [reverseJournalMutation]);

  const updateJournalOptimistically = useCallback(() => {
    // For now, just invalidate queries to refetch data
    queryClient.invalidateQueries({ queryKey: ['ledger', 'journals'] });
  }, [queryClient]);

  return {
    journals,
    loading: isLoading,
    error: error?.message || null,
    filters,
    setFilters,
    cursor,
    nextCursor: currentPageData?.pagination?.nextCursor || null,
    hasNextPage: !!currentPageData?.pagination?.nextCursor,
    total,
    loadMore,
    refreshJournals,
    reverseJournal,
    updateJournalOptimistically,
  };
}
